import {
  isNativeApp
} from './nativeApp';

import {
  getNativeDeviceToken
} from './nativeDeviceAuth';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

function headers(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization:
      'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(response, label) {
  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  return text ? JSON.parse(text) : null;
}

export async function fetchCoursePlacementLabels(courseId) {
  if (!courseId) {
    return {
      streams: {},
      groups: {}
    };
  }

  const encodedCourse =
    encodeURIComponent(courseId);

  const [
    streamResponse,
    groupResponse
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/course_stream_labels?course_id=eq.${encodedCourse}&select=stream_number,name`,
      { headers: headers() }
    ),
    fetch(
      `${SUPABASE_URL}/rest/v1/course_group_labels?course_id=eq.${encodedCourse}&select=stream_number,group_number,name,description`,
      { headers: headers() }
    )
  ]);

  const [
    streamRows,
    groupRows
  ] = await Promise.all([
    readJson(
      streamResponse,
      'Loading Stream names'
    ),
    readJson(
      groupResponse,
      'Loading Group names'
    )
  ]);

  const streams = {};
  const groups = {};

  (Array.isArray(streamRows) ? streamRows : [])
    .forEach(row => {
      streams[String(row.stream_number)] =
        String(row.name || '').trim();
    });

  (Array.isArray(groupRows) ? groupRows : [])
    .forEach(row => {
      groups[
        `${row.stream_number}|${row.group_number}`
      ] = {
        name: String(row.name || '').trim(),
        description:
          String(row.description || '').trim()
      };
    });

  return {
    streams,
    groups
  };
}

export function getStreamDisplayName(
  labels,
  streamNumber
) {
  if (
    streamNumber === null ||
    streamNumber === undefined ||
    streamNumber === ''
  ) {
    return '';
  }

  return (
    labels?.streams?.[
      String(streamNumber)
    ] ||
    `Stream ${streamNumber}`
  );
}

export function getCourseGroupDisplayName(
  labels,
  streamNumber,
  groupNumber
) {
  if (
    groupNumber === null ||
    groupNumber === undefined ||
    groupNumber === ''
  ) {
    return '';
  }

  return (
    labels?.groups?.[
      `${streamNumber}|${groupNumber}`
    ]?.name ||
    `Group ${groupNumber}`
  );
}

export function getCourseGroupDescription(
  labels,
  streamNumber,
  groupNumber
) {
  return (
    labels?.groups?.[
      `${streamNumber}|${groupNumber}`
    ]?.description ||
    ''
  );
}

export async function fetchAssignmentGroupLabelsForCourse(
  courseId
) {
  if (!courseId) return {};

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_labels?select=course_assignment_id,group_number,name,course_assignments!inner(course_id)&course_assignments.course_id=eq.${encodeURIComponent(courseId)}`,
    { headers: headers() }
  );

  const rows = await readJson(
    response,
    'Loading Assignment Group names'
  );

  const labels = {};

  (Array.isArray(rows) ? rows : [])
    .forEach(row => {
      labels[
        `${row.course_assignment_id}|${row.group_number}`
      ] = String(row.name || '').trim();
    });

  return labels;
}

export async function fetchAssignmentGroupDescriptionsForCourse(
  courseId
) {
  if (!courseId) return {};

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_descriptions?select=course_assignment_id,group_number,description,course_assignments!inner(course_id)&course_assignments.course_id=eq.${encodeURIComponent(courseId)}`,
    { headers: headers() }
  );

  const rows = await readJson(
    response,
    'Loading Assignment Group descriptions'
  );

  const descriptions = {};

  (Array.isArray(rows) ? rows : [])
    .forEach(row => {
      descriptions[
        `${row.course_assignment_id}|${row.group_number}`
      ] = String(
        row.description || ''
      ).trim();
    });

  return descriptions;
}


export function getAssignmentGroupDisplayName(
  labels,
  courseAssignmentId,
  groupNumber
) {
  if (
    groupNumber === null ||
    groupNumber === undefined ||
    groupNumber === ''
  ) {
    return '';
  }

  return (
    labels?.[
      `${courseAssignmentId}|${groupNumber}`
    ] ||
    `Group ${groupNumber}`
  );
}

export async function renameInstructorAssignmentGroup({
  cmsToken,
  courseAssignmentId,
  groupNumber,
  name
}) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/save_assignment_group_label_instructor`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        p_course_assignment_id:
          courseAssignmentId,
        p_group_number:
          groupNumber,
        p_name:
          name,
        p_cms_token:
          cmsToken
      })
    }
  );

  const result = await readJson(
    response,
    'Renaming Assignment Group'
  );

  if (!result?.ok) {
    throw new Error(
      result?.message ||
      'The Assignment Group could not be renamed.'
    );
  }

  return result;
}

export async function saveInstructorAssignmentGroupDescription({
  cmsToken,
  courseAssignmentId,
  groupNumber,
  description
}) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/save_assignment_group_description_instructor`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        p_course_assignment_id:
          courseAssignmentId,
        p_group_number:
          groupNumber,
        p_description:
          description,
        p_cms_token:
          cmsToken
      })
    }
  );

  const result = await readJson(
    response,
    'Saving Assignment Group description'
  );

  if (!result?.ok) {
    throw new Error(
      result?.message ||
      'The Assignment Group description could not be saved.'
    );
  }

  return result;
}


export async function renameStudentCourseGroup({
  session,
  courseId,
  streamNumber,
  groupNumber,
  name
}) {
  let deviceToken = null;

  if (isNativeApp()) {
    deviceToken =
      await getNativeDeviceToken();
  }

  const webToken =
    !isNativeApp()
      ? session?.webSessionToken || null
      : null;

  if (!deviceToken && !webToken) {
    throw new Error(
      'Please sign out and sign in again before renaming your Group.'
    );
  }

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/student_rename_course_group`,
      {
        method: 'POST',

        headers:
          headers({
            'Content-Type':
              'application/json'
          }),

        body:
          JSON.stringify({
            p_course_id:
              courseId,

            p_stream_number:
              streamNumber,

            p_group_number:
              groupNumber,

            p_name:
              name,

            p_web_token:
              webToken,

            p_device_token:
              deviceToken
          })
      }
    );

  const result =
    await readJson(
      response,
      'Renaming Course Group'
    );

  if (!result?.ok) {
    throw new Error(
      result?.message ||
      'The Course Group could not be renamed.'
    );
  }

  return result;
}


export async function renameStudentAssignmentGroup({
  session,
  courseAssignmentId,
  groupNumber,
  name
}) {
  let deviceToken = null;

  if (isNativeApp()) {
    deviceToken =
      await getNativeDeviceToken();
  }

  const webToken =
    !isNativeApp()
      ? session?.webSessionToken || null
      : null;

  if (!deviceToken && !webToken) {
    throw new Error(
      'Please sign out and sign in again before renaming your Group.'
    );
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/student_rename_assignment_group`,
    {
      method: 'POST',
      headers: headers({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify({
        p_course_assignment_id:
          courseAssignmentId,
        p_group_number:
          groupNumber,
        p_name:
          name,
        p_web_token:
          webToken,
        p_device_token:
          deviceToken
      })
    }
  );

  const result = await readJson(
    response,
    'Renaming Assignment Group'
  );

  if (!result?.ok) {
    throw new Error(
      result?.message ||
      'The Assignment Group could not be renamed.'
    );
  }

  return result;
}
